/**
 * Maps human-readable keys to translation IDs for the /tools/bmr-calculator
 * SEO landing page. New content (not from Framer XLIFF), so keys carry the
 * `bmr_` prefix directly.
 *
 * The page is gated to CH + DE, in de/en/fr where the country offers them,
 * so `de-DE` is held to full parity on this namespace. `de-AT`, `en-NL` and
 * `nl` still exempt it in `SKIP_NAMESPACES_PER_LOCALE`
 * (scripts/lint-mirror-locales.ts) — translate + drop an exemption when the
 * page opens to that market.
 */
export const bmrCalculatorKeys = {
  // Metadata
  metaTitle: "bmr_metaTitle",
  metaDescription: "bmr_metaDescription",

  // Hero
  heading: "bmr_heading",
  subheading: "bmr_subheading",

  // Calculator widget
  calcTitle: "bmr_calcTitle",
  calcSexLabel: "bmr_calcSexLabel",
  calcSexFemale: "bmr_calcSexFemale",
  calcSexMale: "bmr_calcSexMale",
  calcSexHint: "bmr_calcSexHint",
  calcAgeLabel: "bmr_calcAgeLabel",
  calcHeightLabel: "bmr_calcHeightLabel",
  calcWeightLabel: "bmr_calcWeightLabel",
  calcActivityLabel: "bmr_calcActivityLabel",
  calcUnitYears: "bmr_calcUnitYears",
  calcUnitCm: "bmr_calcUnitCm",
  calcUnitKg: "bmr_calcUnitKg",
  calcSubmit: "bmr_calcSubmit",
  calcReset: "bmr_calcReset",
  calcEmptyState: "bmr_calcEmptyState",
  calcOutOfRange: "bmr_calcOutOfRange",
  calcBmrLabel: "bmr_calcBmrLabel",
  calcKcalPerDay: "bmr_calcKcalPerDay",
  calcTdeeLabel: "bmr_calcTdeeLabel",
  calcTdeeHint: "bmr_calcTdeeHint",
  calcCompareLabel: "bmr_calcCompareLabel",
  calcCompareHint: "bmr_calcCompareHint",
  calcDisclaimer: "bmr_calcDisclaimer",

  // Quiz hand-off, shown in the result panel once Calculate has run
  quizHeading: "bmr_quizHeading",
  quizBody: "bmr_quizBody",
  quizCta: "bmr_quizCta",

  // Activity levels (shared by the widget selector and the reference table)
  activitySedentary: "bmr_activitySedentary",
  activitySedentaryDesc: "bmr_activitySedentaryDesc",
  activityLight: "bmr_activityLight",
  activityLightDesc: "bmr_activityLightDesc",
  activityModerate: "bmr_activityModerate",
  activityModerateDesc: "bmr_activityModerateDesc",
  activityVery: "bmr_activityVery",
  activityVeryDesc: "bmr_activityVeryDesc",
  activityExtra: "bmr_activityExtra",
  activityExtraDesc: "bmr_activityExtraDesc",

  // What is BMR
  whatLabel: "bmr_whatLabel",
  whatHeading: "bmr_whatHeading",
  whatP1: "bmr_whatP1",
  whatP2: "bmr_whatP2",
  whatP3: "bmr_whatP3",

  // Formulas
  formulaHeading: "bmr_formulaHeading",
  formulaIntro: "bmr_formulaIntro",
  formulaMifflinTitle: "bmr_formulaMifflinTitle",
  formulaMifflinMale: "bmr_formulaMifflinMale",
  formulaMifflinFemale: "bmr_formulaMifflinFemale",
  formulaMifflinExample: "bmr_formulaMifflinExample",
  formulaHarrisTitle: "bmr_formulaHarrisTitle",
  formulaHarrisMale: "bmr_formulaHarrisMale",
  formulaHarrisFemale: "bmr_formulaHarrisFemale",
  formulaHarrisExample: "bmr_formulaHarrisExample",
  formulaWhyTwo: "bmr_formulaWhyTwo",

  // BMR to daily calories
  tdeeHeading: "bmr_tdeeHeading",
  tdeeIntro: "bmr_tdeeIntro",
  tdeeColLevel: "bmr_tdeeColLevel",
  tdeeColFactor: "bmr_tdeeColFactor",
  tdeeColMeaning: "bmr_tdeeColMeaning",
  tdeeExample: "bmr_tdeeExample",
  tdeeNote: "bmr_tdeeNote",

  // Limitations
  limitsLabel: "bmr_limitsLabel",
  limitsHeading: "bmr_limitsHeading",
  limitsIntro: "bmr_limitsIntro",
  limit1Title: "bmr_limit1Title",
  limit1Body: "bmr_limit1Body",
  limit2Title: "bmr_limit2Title",
  limit2Body: "bmr_limit2Body",
  limit3Title: "bmr_limit3Title",
  limit3Body: "bmr_limit3Body",
  limit4Title: "bmr_limit4Title",
  limit4Body: "bmr_limit4Body",

  // What actually moves your rate
  beyondLabel: "bmr_beyondLabel",
  beyondHeading: "bmr_beyondHeading",
  beyondIntro: "bmr_beyondIntro",
  beyond1Title: "bmr_beyond1Title",
  beyond1Body: "bmr_beyond1Body",
  beyond2Title: "bmr_beyond2Title",
  beyond2Body: "bmr_beyond2Body",
  beyond3Title: "bmr_beyond3Title",
  beyond3Body: "bmr_beyond3Body",
  beyond4Title: "bmr_beyond4Title",
  beyond4Body: "bmr_beyond4Body",

  // Membership section
  memLabel: "bmr_memLabel",
  memHeading: "bmr_memHeading",
  memBody: "bmr_memBody",
  memFeature1: "bmr_memFeature1",
  memFeature2: "bmr_memFeature2",
  memFeature3: "bmr_memFeature3",
  memFeature4: "bmr_memFeature4",
  memFeature5: "bmr_memFeature5",
  memCtaPrimary: "bmr_memCtaPrimary",
  memCtaSecondary: "bmr_memCtaSecondary",
  memFootnote: "bmr_memFootnote",

  // FAQ fallback (used until page-tagged FAQs exist in the CMS)
  faqHeading: "bmr_faqHeading",
  faqQ1: "bmr_faqQ1",
  faqA1: "bmr_faqA1",
  faqQ2: "bmr_faqQ2",
  faqA2: "bmr_faqA2",
  faqQ3: "bmr_faqQ3",
  faqA3: "bmr_faqA3",
  faqQ4: "bmr_faqQ4",
  faqA4: "bmr_faqA4",
  faqQ5: "bmr_faqQ5",
  faqA5: "bmr_faqA5",
  faqQ6: "bmr_faqQ6",
  faqA6: "bmr_faqA6",

  // Closing CTA
  ctaHeading: "bmr_ctaHeading",
  ctaBody: "bmr_ctaBody",
  ctaPrimary: "bmr_ctaPrimary",
  ctaSecondary: "bmr_ctaSecondary",

  // Legal / medical
  disclaimer: "bmr_disclaimer",
} as const
